import { Link } from "react-router-dom";
import { Check, IdCard, ShieldCheck, FileBadge, Award, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MarketingNav } from "@/components/Marketing/MarketingNav";
import { MarketingFooter } from "@/components/Marketing/MarketingFooter";
import { MarketingFinalCta } from "@/components/Marketing/MarketingFinalCta";

const STEPS = [
  {
    icon: ShieldCheck,
    title: "Create your profile, once",
    body: "Create one account with an email and password. Add your outlet, beats and KYC documents once, then reuse that profile across every institution you cover.",
  },
  {
    icon: IdCard,
    title: "Request accreditation & passes",
    body: "Apply for events and standing accreditation from your portal. Selection is fair, transparent, and geo-capped where an institution requires it, you can see exactly why a decision was made.",
  },
  {
    icon: FileBadge,
    title: "Submit coverage",
    body: "Drop in your published article or clip after an event. It's scored automatically on accuracy, tone, reach and quality, and you can see your own scoring history over time.",
  },
  {
    icon: Award,
    title: "Get recognised",
    body: "Institutions running press awards programmes can nominate or shortlist you directly from your coverage history, no separate application process.",
  },
];

const BENEFITS = [
  "One account across every institution you cover",
  "One profile, all your accreditations",
  "Coverage submissions auto-scored",
  "Fair event selection, geo-capped & transparent",
  "Works across every institution on CONORBIT",
  "Your scoring history travels with you",
];

export default function JournalistsPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <MarketingNav />

      <section className="relative pt-32 pb-20 lg:pt-44 lg:pb-24 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="relative rounded-3xl overflow-hidden border border-border gradient-emerald-radial text-ivory p-10 md:p-16 grid lg:grid-cols-2 gap-12 items-center">
            <div className="absolute inset-0 bg-grid-emerald opacity-30" aria-hidden />
            <div className="relative">
              <span className="text-[11px] uppercase tracking-[0.25em] text-accent">For journalists</span>
              <h1 className="font-display text-4xl md:text-5xl mt-3 text-ivory leading-tight">
                Your press credentials,<br />
                <span className="text-gradient-gold">one tap away.</span>
              </h1>
              <p className="text-justify text-ivory/75 mt-5 max-w-md leading-relaxed">
                Sign in once. Hold every accreditation, request, pass and
                submission across all the institutions you cover, in one portal.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/portal/register">
                  <Button size="lg" variant="default" className="bg-accent text-accent-foreground hover:bg-accent/90 h-12 px-7">
                    Create journalist account
                  </Button>
                </Link>
                <Link to="/portal/login">
                  <Button size="lg" variant="outline" className="border-accent/40 text-ivory hover:bg-accent/10 h-12 px-7 bg-transparent">
                    Sign in
                  </Button>
                </Link>
              </div>
            </div>
            <ul className="relative space-y-4">
              {BENEFITS.map((t) => (
                <li key={t} className="flex items-start gap-3 bg-ivory/5 border border-ivory/10 rounded-xl p-4 backdrop-blur-sm">
                  <Check className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                  <span className="text-ivory/90 text-sm">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mb-12">
            <h2 className="font-display text-3xl md:text-4xl text-foreground">How the portal works</h2>
            <p className="text-justify text-muted-foreground mt-3 leading-relaxed">
              One account, no per-institution sign-ups. From your first sign-in
              to your next press award nomination, here's the path.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {STEPS.map((s) => (
              <div key={s.title} className="rounded-2xl border border-border bg-card p-6 hover-scale">
                <s.icon className="w-6 h-6 text-accent mb-3" />
                <h3 className="font-display text-lg text-foreground">{s.title}</h3>
                <p className="text-justify text-sm text-muted-foreground mt-2 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-t border-border bg-muted/20">
        <div className="container mx-auto px-4 max-w-2xl text-center">
          <BarChart3 className="w-8 h-8 text-accent mx-auto mb-4" />
          <h2 className="font-display text-2xl md:text-3xl text-foreground">Your coverage score follows you</h2>
          <p className="text-justify text-muted-foreground mt-3 leading-relaxed">
            Because your profile is portable across every institution on CONORBIT, a strong
            track record with one press office carries weight with the next, instead of
            starting from zero every time you cover a new beat.
          </p>
        </div>
      </section>

      <MarketingFinalCta />
      <MarketingFooter />
    </div>
  );
}
